import type { IStoreInfoFormShape } from '../StoreDetails/storeDetails.types';
import { convertUtcIsoStringToStoreCheckoutCalendarDate } from './storeCheckoutDate.utils';
import { normalizeBulkPricingType } from './storeCheckoutBulkPricingTypeUpdate.utils';
import {
  resolveStoreShippingOptionFromStore,
  type StoreShippingFormOption,
} from './storeCheckoutShippingTypeUpdate.utils';
import { buildStoreReviewDonationAmountsFromCartItems } from './storeReviewDonationValidation.utils';
import { normalizeStoreType } from '@/modules/MostRecent/MostRecentActivity';
import { getStoreCartDesignGroupKey } from '@/modules/Cart/StoreCart/helpers/storeCartDesignGroups.utils';
import { readPersistedStoreCartDesignGroupMinimums } from '@/modules/Cart/StoreCart/helpers/storeCartNavigation.utils';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import type { IDesignLicenses, IDesignPricing } from '@/common/interfaces/design.interface';
import type { IQuoteBreakpoints } from '@/common/interfaces/quoter.interface';
import type { IStoreOrderItem, IStoreResponse } from '@/common/interfaces/store.interface';
import { s3FileUrlGetter } from '@/utilities/CommonUtils';

const CHECKOUT_STORE_TYPES = [
  'group-order',
  'brand-drop',
  'fundraiser',
  'permanent-store',
] as const;

export type StoreCheckoutStoreType = (typeof CHECKOUT_STORE_TYPES)[number];

export const resolveStoreCheckoutOrderIdFromUrl = (): string | null => {
  const searchParams = new URLSearchParams(globalThis.location.search);
  const storeId = searchParams.get('storeId');

  if (storeId && /^\d+$/.test(storeId)) {
    return storeId;
  }

  const editRouteMatch = globalThis.location.pathname.match(/\/store\/edit\/(\d+)/);
  if (editRouteMatch?.[1]) {
    return editRouteMatch[1];
  }

  return null;
};

export const isStoreCheckoutEditPage = (): boolean =>
  globalThis.location.pathname.includes('/store/edit/');

type IStoreOrderItemApi = IStoreOrderItem &
  Record<string, unknown> & {
    proof_title?: string;
    proof_id?: number;
    proof_item?: Record<string, unknown>;
    design?: Record<string, unknown>;
    apparel_style_code?: string;
    apparelStyleCode?: string;
    apparel_name?: string | null;
    apparelName?: string;
    apparel_brand?: string;
    apparelBrand?: string;
    apparel_color?: string;
    apparelColor?: string;
    apparel_hex?: string;
    apparelHex?: string;
    apparel_color_code?: string;
    apparelColorCode?: string;
    apparel_sizes?: string;
    apparelSizes?: string;
    front_image?: string;
    frontImage?: string;
    back_image?: string;
    backImage?: string;
    primary_image_url?: string;
    primaryImage?: string;
    design_img_primary?: string;
    designImgPrimary?: string;
    design_img_secondary?: string;
    designImgSecondary?: string;
    design_image_primary?: string;
    design_image_secondary?: string;
    print_types?: string;
    printTypes?: string;
    print_type_info?: ICartItemDetail['printTypeInfo'];
    printTypeInfo?: ICartItemDetail['printTypeInfo'];
    decor_method_info?: ICartItemDetail['decorMethodInfo'];
    decorMethodInfo?: ICartItemDetail['decorMethodInfo'];
    decorating_method?: string;
    decoratingMethod?: string;
    ship_type?: string;
    shipType?: string;
    notes?: string;
    is_flash_eligible?: boolean;
    isFlashEligible?: boolean;
    is_made_to_order?: boolean;
    isMadeToOrder?: boolean;
    min_quant?: number;
    minQuant?: number;
    alpha_numeric_proof_item_id?: string;
    alphaNumericProofItemId?: string;
    proof_item_version_name?: string;
    proofItemVersionName?: string;
    productTitle?: string;
    productDescription?: string;
    fundraiser_amount?: string;
    fundraiserAmount?: string;
    donation_amount?: string;
    donationAmount?: string;
    markup_amount?: string;
    markupAmount?: string;
    breakpoints?: Array<Record<string, unknown>>;
    pricing_type?: string;
    pricingType?: string;
    minimum_quantity?: number;
    minimumQuantity?: number;
    group_order_breakpoints?: string;
    groupOrderBreakpoints?: string;
  };

const readNestedRecord = (value: unknown): Record<string, unknown> | undefined =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

/**
 * Catalog apparel may live under design.apparel, item.apparel, proof_item.apparel,
 * or apparelData. Prefer collecting every candidate so a sparse first object
 * (e.g. design.apparel = { style_code }) does not hide name/brand on another.
 * A string apparel value is treated as the apparel name.
 */
const collectApparelRecords = (item: IStoreOrderItemApi): Record<string, unknown>[] => {
  const proofItem = readNestedRecord(item.proof_item);
  const design = readNestedRecord(item.design);
  const itemRecord = item as Record<string, unknown>;
  const candidates = [
    design?.apparel,
    itemRecord.apparel,
    proofItem?.apparel,
    design?.apparelData,
    itemRecord.apparelData,
    proofItem?.apparelData,
  ];

  return candidates
    .map((candidate) => {
      if (typeof candidate === 'string' && candidate.trim()) {
        return { name: candidate.trim() };
      }

      return readNestedRecord(candidate);
    })
    .filter((source): source is Record<string, unknown> => source != null);
};

const readField = (item: IStoreOrderItemApi, ...keys: string[]): unknown => {
  const readFromRecord = (record: Record<string, unknown>): unknown => {
    const matchedKey = keys.find((key) => {
      const value = record[key];
      return value != null && value !== '';
    });

    return matchedKey != null ? record[matchedKey] : undefined;
  };

  const directValue = readFromRecord(item);
  if (directValue != null) {
    return directValue;
  }

  const proofItem = readNestedRecord(item.proof_item);
  const design = readNestedRecord(item.design);
  const apparelRecords = collectApparelRecords(item);
  // Prefer apparel records before design/proof_item so generic keys like `name`
  // / `brand` resolve from apparel.name / apparel.brand, not design.name.
  const nestedSources = [...apparelRecords, proofItem, design].filter(
    (source): source is Record<string, unknown> => source != null
  );

  return nestedSources.reduce<unknown>(
    (found, source) => found ?? readFromRecord(source),
    undefined
  );
};

const readString = (value: unknown): string => (typeof value === 'string' ? value : '');

/** Prefer explicit apparel_* fields, then apparel.name (never design.name alone). */
const readApparelCatalogName = (item: IStoreOrderItemApi): string => {
  const explicitName = readString(readField(item, 'apparel_name', 'apparelName'));
  if (explicitName.trim()) {
    return explicitName.trim();
  }

  const nestedApparelName = collectApparelRecords(item)
    .map((apparel) =>
      readString(apparel.name ?? apparel.apparel_name ?? apparel.apparelName).trim()
    )
    .find((name) => Boolean(name));

  return nestedApparelName ?? '';
};

const EMPTY_DESIGN_LICENSING: IDesignLicenses = {
  id: 0,
  collegiate_status: 'no_marks',
  greek_status: 'no_marks',
  collegiate_comment: '',
  greek_comment: '',
  design_id: 0,
  collegiate_duplicate_item_id: 0,
  greek_duplicate_item_id: 0,
  has_collegiate_mark: 0,
  has_greek_mark: 0,
  collegiate_comment_by: '',
  greek_comment_by: '',
};

const EMPTY_DESIGN_PRICING: IDesignPricing = {
  id: 0,
  block_self_checkout: false,
  design_id: 0,
  proof_item_id: 0,
  discount: 0,
  discount_quantity: 0,
  quantity: 0,
  ship_type: '',
};

const readDesignApparelSizes = (item: IStoreOrderItemApi): string => {
  const design = readNestedRecord(item.design);
  const apparel = design ? readNestedRecord(design.apparel) : undefined;

  if (apparel) {
    const nestedSizes = readString(apparel.sizes ?? apparel.apparel_sizes ?? apparel.apparelSizes);

    if (nestedSizes) {
      return nestedSizes;
    }
  }

  return readString(
    readField(item, 'apparel_sizes', 'apparelSizes', 'allowed_sizes', 'allowedSizes')
  );
};

const readOrderItemSizes = (item: IStoreOrderItemApi): string => readString(item.sizes);

const readNumber = (value: unknown, fallback = 0): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

export const parseStoreOrderGroupBreakpoints = (breakpoints: string): IQuoteBreakpoints[] => {
  if (!breakpoints.trim()) {
    return [];
  }

  const seenQuantities = new Set<number>();

  return breakpoints
    .split(';')
    .map((segment) => {
      const [quantityPart, pricePart] = segment.split(':');
      const quantity = Number(quantityPart);
      const finalPricePerUnit = Number(pricePart);

      if (
        !Number.isFinite(quantity) ||
        quantity <= 0 ||
        !Number.isFinite(finalPricePerUnit) ||
        seenQuantities.has(quantity)
      ) {
        return null;
      }

      seenQuantities.add(quantity);
      return { quantity, finalPricePerUnit };
    })
    .filter((breakpoint): breakpoint is IQuoteBreakpoints => breakpoint != null);
};

export const getStoreOrderItemApiMinimumQuantity = (item: ICartItemDetail): number | undefined => {
  const savedMinimum = Number(item.savedMinimumQuantity ?? 0);

  if (savedMinimum > 0) {
    return savedMinimum;
  }

  return undefined;
};

const IMAGE_SIDE_TOKENS = new Set(['front', 'back']);

const isImageSideToken = (value: string): boolean =>
  IMAGE_SIDE_TOKENS.has(value.trim().toLowerCase());

/** Absolute URL or CloudFront designs path — never treat primary_image side tokens as URLs. */
const toDesignImageUrl = (path: string): string => {
  if (!path || isImageSideToken(path)) {
    return '';
  }

  if (/^https?:\/\//i.test(path) || path.startsWith('//') || path.startsWith('data:')) {
    return path;
  }

  return s3FileUrlGetter(path);
};

const readImageFieldFromSources = (
  sources: Array<Record<string, unknown> | undefined>,
  ...keys: string[]
): string => {
  const matched = sources
    .flatMap((source) => {
      if (!source) {
        return [];
      }

      return keys.map((key) => readString(source[key]));
    })
    .find((value) => Boolean(value) && !isImageSideToken(value));

  return matched ?? '';
};

/**
 * Resolve garment mockup URLs for store checkout.
 * Prefer designed mockups (`nt_client_*` / `client_*`) over apparel catalog blanks —
 * edit-store GET payloads often nest selected color on apparel while `apparel.frontImage`
 * is still a default blank color.
 */
const resolvePrimaryImageUrls = (
  item: IStoreOrderItemApi
): { primaryImage: string; frontImage: string; backImage: string } => {
  const design = readNestedRecord(item.design);
  const proofItem = readNestedRecord(item.proof_item);
  // Do not search apparel records here — those blanks hide designed mockups on edit.
  const garmentSources: Array<Record<string, unknown> | undefined> = [
    item as Record<string, unknown>,
    proofItem,
    design,
  ];

  const explicitFront = readImageFieldFromSources(
    [item as Record<string, unknown>, proofItem],
    'front_image',
    'frontImage'
  );
  const explicitBack = readImageFieldFromSources(
    [item as Record<string, unknown>, proofItem],
    'back_image',
    'backImage'
  );
  const designedFront = toDesignImageUrl(
    readImageFieldFromSources(
      garmentSources,
      'nt_client_front_image',
      'client_front_image',
      'ntClientFrontImage',
      'clientFrontImage'
    )
  );
  const designedBack = toDesignImageUrl(
    readImageFieldFromSources(
      garmentSources,
      'nt_client_back_image',
      'client_back_image',
      'ntClientBackImage',
      'clientBackImage'
    )
  );
  const imageUrl = toDesignImageUrl(
    readImageFieldFromSources(garmentSources, 'primary_image_url', 'image_url', 'imageUrl')
  );

  const primarySideRaw = readString(item.primary_image);
  const primarySideFallback =
    typeof item.primaryImage === 'string' && isImageSideToken(item.primaryImage)
      ? item.primaryImage
      : '';
  const primarySide = (primarySideRaw || primarySideFallback).toLowerCase();

  // Designed mockups first — order-item front_image can still be a blank catalog URL.
  const frontImage = designedFront || explicitFront;
  const backImage = designedBack || explicitBack;

  if (frontImage || backImage) {
    return {
      primaryImage: primarySide === 'back' ? backImage || frontImage : frontImage || backImage,
      frontImage,
      backImage,
    };
  }

  if (imageUrl) {
    const isBack = primarySide === 'back' || imageUrl.toLowerCase().includes('back');
    return {
      primaryImage: imageUrl,
      frontImage: isBack ? '' : imageUrl,
      backImage: isBack ? imageUrl : '',
    };
  }

  // Last resort: apparel catalog blanks when designed mockups are absent.
  const apparelRecords = collectApparelRecords(item);
  const apparelFront = toDesignImageUrl(
    readImageFieldFromSources(apparelRecords, 'front_image', 'frontImage')
  );
  const apparelBack = toDesignImageUrl(
    readImageFieldFromSources(apparelRecords, 'back_image', 'backImage')
  );

  if (apparelFront || apparelBack) {
    return {
      primaryImage:
        primarySide === 'back' ? apparelBack || apparelFront : apparelFront || apparelBack,
      frontImage: apparelFront,
      backImage: apparelBack,
    };
  }

  return {
    primaryImage: '',
    frontImage: '',
    backImage: '',
  };
};

const normalizeHydratedDonationAmount = (rawAmount: unknown): string => {
  if (typeof rawAmount === 'number') {
    if (!Number.isFinite(rawAmount) || rawAmount <= 0) {
      return '';
    }

    return String(rawAmount);
  }

  if (typeof rawAmount !== 'string' || !rawAmount.trim()) {
    return '';
  }

  const parsed = Number.parseFloat(rawAmount.trim());

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return '';
  }

  return String(parsed);
};

const readDonationAmountFromBreakpoints = (item: IStoreOrderItemApi): string => {
  const itemRecord = item as Record<string, unknown>;
  const nestedPricing = readNestedRecord(itemRecord.pricing);
  const nestedOrderItem = readNestedRecord(itemRecord.order_item);
  const breakpointCandidates = [
    item.breakpoints,
    itemRecord.Breakpoints,
    nestedPricing?.breakpoints,
    nestedOrderItem?.breakpoints,
  ];

  const breakpoints = breakpointCandidates.find(
    (candidate): candidate is Array<Record<string, unknown>> =>
      Array.isArray(candidate) && candidate.length > 0
  );

  if (!breakpoints) {
    return '';
  }

  const amountFromBreakpoint = breakpoints
    .map((breakpoint) => {
      if (breakpoint == null || typeof breakpoint !== 'object') {
        return '';
      }

      const record = breakpoint as Record<string, unknown>;

      return normalizeHydratedDonationAmount(
        record.donation_amount ??
          record.donationAmount ??
          record.markup_amount ??
          record.markupAmount ??
          record.fundraiser_amount ??
          record.fundraiserAmount
      );
    })
    .find((amount) => Boolean(amount));

  return amountFromBreakpoint ?? '';
};

const readStoreOrderItemDonationAmount = (item: IStoreOrderItemApi): string => {
  const topLevelAmount = normalizeHydratedDonationAmount(
    readField(
      item,
      'fundraiser_amount',
      'fundraiserAmount',
      'donation_amount',
      'donationAmount',
      'markup_amount',
      'markupAmount'
    )
  );

  if (topLevelAmount) {
    return topLevelAmount;
  }

  // Store GET persists donation/markup on pricing breakpoints, not always on the order item root.
  return readDonationAmountFromBreakpoints(item);
};

export const resolveCheckoutStoreType = (
  storeType?: string | null
): StoreCheckoutStoreType | '' => {
  if (storeType == null || storeType.trim() === '') {
    return '';
  }

  const normalized = normalizeStoreType(storeType);

  if (normalized === 'permanent' || normalized.includes('permanent')) {
    return 'permanent-store';
  }

  const matched = CHECKOUT_STORE_TYPES.find(
    (type) => normalized === type || normalized.includes(type.replaceAll('-', ''))
  );

  return matched ?? '';
};

export const mapStoreOrderItemToCartItem = (
  item: IStoreOrderItemApi,
  store: IStoreResponse
): ICartItemDetail => {
  const proofItemId = readNumber(readField(item, 'proof_item_id', 'proofItemId') ?? item.id);
  const designId = readNumber(readField(item, 'design_id', 'designId'));
  const designImageSources: Array<Record<string, unknown> | undefined> = [
    item as Record<string, unknown>,
    readNestedRecord(item.proof_item),
    readNestedRecord(item.design),
  ];
  const images = resolvePrimaryImageUrls(item);
  const designApparelSizes = readDesignApparelSizes(item);
  const orderItemSizes = readOrderItemSizes(item);
  const apparelSizes = designApparelSizes || orderItemSizes;
  const price = readNumber(readField(item, 'price'));
  const apiMinimumQuantity = readNumber(readField(item, 'minimum_quantity', 'minimumQuantity'));
  const groupOrderBreakpoints = readString(
    readField(item, 'group_order_breakpoints', 'groupOrderBreakpoints')
  );
  const parsedPriceList = parseStoreOrderGroupBreakpoints(groupOrderBreakpoints);
  const quantity = readNumber(readField(item, 'num_units', 'numUnits', 'quantity'), 12);
  const minQuant =
    apiMinimumQuantity > 0
      ? apiMinimumQuantity
      : readNumber(readField(item, 'min_quant', 'minQuant'), quantity > 0 ? quantity : 12);
  const resolvedUnitPrice =
    parsedPriceList.find((breakpoint) => breakpoint.quantity === minQuant)?.finalPricePerUnit ??
    price;
  const productTitle = readString(
    readField(item, 'product_title', 'productTitle', 'proof_title', 'proofTitle')
  ).trim();
  const apparelName = readApparelCatalogName(item);
  const resolvedProductTitle = productTitle || apparelName || undefined;
  const printTypeInfo = readField(item, 'print_type_info', 'printTypeInfo');
  const decorMethodInfo = readField(item, 'decor_method_info', 'decorMethodInfo');
  const productDescription = readString(
    readField(
      item,
      'product_description',
      'productDescription',
      'description',
      'apparel_description',
      'apparelDescription'
    )
  );
  const pricingTypeValue = readField(item, 'pricing_type', 'pricingType');
  const pricingType = normalizeBulkPricingType(pricingTypeValue);

  return {
    id: readNumber(readField(item, 'id'), proofItemId),
    notes: readString(readField(item, 'notes')),
    shipType: readString(readField(item, 'ship_type', 'shipType')) || 'standard',
    proofTitle: resolvedProductTitle || store.name,
    proofId: readNumber(readField(item, 'proof_id', 'proofId')),
    designId,
    designGroupItemId: readNumber(readField(item, 'design_group_id', 'designGroupId')) || undefined,
    proofItemId,
    apparelStyleCode: readString(
      readField(item, 'apparel_style_code', 'apparelStyleCode', 'style_code', 'styleCode')
    ),
    apparelName: apparelName || productTitle,
    // Empty product_title on edit/live stores should still show apparel.name in the title input.
    productTitle: resolvedProductTitle,
    productDescription,
    apparelBrand: readString(readField(item, 'apparel_brand', 'apparelBrand', 'brand')),
    apparelColor: readString(
      readField(item, 'apparel_color', 'apparelColor', 'color', 'colorName', 'color_name')
    ),
    apparelColorCode: readString(
      readField(item, 'apparel_color_code', 'apparelColorCode', 'colorCode')
    ),
    apparelHex: readString(
      readField(
        item,
        'apparel_hex',
        'apparelHex',
        'hex',
        'hex_code',
        'hexCode',
        'color_hex',
        'colorHex'
      )
    ),
    apparelSizes,
    designApparelSizes: designApparelSizes || apparelSizes,
    orderItemSizes,
    apparelPrice: resolvedUnitPrice,
    price: resolvedUnitPrice,
    quantity,
    isFlashEligible: Boolean(readField(item, 'is_flash_eligible', 'isFlashEligible')),
    isMadeToOrder:
      store.is_made_to_order || Boolean(readField(item, 'is_made_to_order', 'isMadeToOrder')),
    isStore: true,
    printTypes: readString(readField(item, 'print_types', 'printTypes')),
    decoratingMethod: readString(readField(item, 'decorating_method', 'decoratingMethod')),
    primaryImage: images.primaryImage,
    frontImage: images.frontImage,
    backImage: images.backImage,
    designImgPrimary: toDesignImageUrl(
      readImageFieldFromSources(
        designImageSources,
        'design_img_primary',
        'designImgPrimary',
        'design_image_primary'
      )
    ),
    designImgSecondary: toDesignImageUrl(
      readImageFieldFromSources(
        designImageSources,
        'design_img_secondary',
        'designImgSecondary',
        'design_image_secondary'
      )
    ),
    hasCustomNames: false,
    hasCustomNumbers: false,
    designLicensing: {
      ...EMPTY_DESIGN_LICENSING,
      design_id: designId,
      has_collegiate_mark: readNumber(item.collegiate_licensed),
      has_greek_mark: readNumber(item.greek_licensed),
    },
    isExternalApparel: Boolean(item.is_external_apparel),
    printTypeVariant: null,
    decoratingVariant: null,
    minQuant,
    savedMinimumQuantity: apiMinimumQuantity > 0 ? apiMinimumQuantity : undefined,
    cartItemId: readNumber(readField(item, 'id', 'cart_item_id', 'cartItemId'), proofItemId),
    priceList: parsedPriceList,
    proofItemVersionName: readString(
      readField(item, 'proof_item_version_name', 'proofItemVersionName')
    ),
    alphaNumericProofItemId: readString(
      readField(item, 'alpha_numeric_proof_item_id', 'alphaNumericProofItemId')
    ),
    apparelCategory: readString(readField(item, 'apparel_category', 'apparelCategory')),
    apparelWeight: readNumber(readField(item, 'apparel_weight', 'apparelWeight')),
    apparelType: readNumber(readField(item, 'apparel_type', 'apparelType')),
    discount: 0,
    printTypeInfo: Array.isArray(printTypeInfo) ? printTypeInfo : [],
    decorMethodInfo: Array.isArray(decorMethodInfo) ? decorMethodInfo : [],
    fromDesignTool: 0,
    organizationId: 0,
    collegiateId: 0,
    isStreamlinedProofItem: false,
    strikedPricePerUnit: 0,
    standardShipPrice: 0,
    flashShipPrice: 0,
    economyShipPrice: 0,
    expeditedShipPrice: 0,
    designPricing: {
      ...EMPTY_DESIGN_PRICING,
      design_id: designId,
      proof_item_id: proofItemId,
    },
    pricingType,
    isActive: true,
    is_selected: 1,
    donationAmount: readStoreOrderItemDonationAmount(item),
  };
};

export const mapStoreOrderItemsToCartItems = (store: IStoreResponse): ICartItemDetail[] =>
  (store.orderItems ?? []).map((item) =>
    mapStoreOrderItemToCartItem(item as IStoreOrderItemApi, store)
  );

/** Cart quantity/minQuant from the store cart drawer should win over sparse store defaults. */
export const resolveMergedStoreCheckoutCartMinimum = (
  cartMatch: ICartItemDetail,
  storeItem: ICartItemDetail
): number | undefined => {
  const storeApiMinimum = getStoreOrderItemApiMinimumQuantity(storeItem);

  if (storeApiMinimum != null) {
    return storeApiMinimum;
  }

  const cartMinimum = cartMatch.minQuant ?? cartMatch.quantity;

  if (typeof cartMinimum === 'number' && cartMinimum > 0) {
    return cartMinimum;
  }

  return undefined;
};

const findCartItemMatch = (
  storeItem: ICartItemDetail,
  cartItems: ICartItemDetail[]
): ICartItemDetail | undefined => {
  const proofItemIdMatch = cartItems.find((item) => item.proofItemId === storeItem.proofItemId);
  if (proofItemIdMatch) {
    return proofItemIdMatch;
  }

  const designIdMatch = cartItems.find((item) => item.designId === storeItem.designId);
  if (designIdMatch) {
    return designIdMatch;
  }

  return cartItems.find(
    (item) =>
      item.designGroupItemId != null &&
      storeItem.designGroupItemId != null &&
      item.designGroupItemId === storeItem.designGroupItemId
  );
};

/** Store GET items are sparse — merge with cart lines for titles, images, and apparel details. */
export const mergeStoreCheckoutCartItems = (
  store: IStoreResponse,
  cartItems: ICartItemDetail[]
): ICartItemDetail[] => {
  const storeItems = mapStoreOrderItemsToCartItems(store);

  if (!storeItems.length) {
    return [];
  }

  if (!cartItems.length) {
    return storeItems;
  }

  const persistedDesignGroupMinimums = readPersistedStoreCartDesignGroupMinimums();
  const usedCartItemIds = new Set<number>();

  return storeItems.map((storeItem) => {
    const cartMatch = findCartItemMatch(storeItem, cartItems);

    if (!cartMatch) {
      return storeItem;
    }

    usedCartItemIds.add(cartMatch.id);

    const designGroupKey = getStoreCartDesignGroupKey(cartMatch);
    const persistedMinimum = persistedDesignGroupMinimums[designGroupKey];
    const mergedCartMinimum =
      resolveMergedStoreCheckoutCartMinimum(cartMatch, storeItem) ??
      persistedMinimum ??
      (storeItem.minQuant > 0 ? storeItem.minQuant : undefined) ??
      (storeItem.quantity > 0 ? storeItem.quantity : undefined);

    const shouldPreferStoreItemPricing =
      Boolean(storeItem.priceList?.length) &&
      getStoreOrderItemApiMinimumQuantity(storeItem) != null;
    const shouldPreferCartPricing = Boolean(cartMatch.priceList?.length);

    let mergedPriceList = storeItem.priceList;
    let mergedPrice = storeItem.price || cartMatch.price;
    let mergedApparelPrice = storeItem.apparelPrice || cartMatch.apparelPrice;

    if (shouldPreferStoreItemPricing) {
      mergedPriceList = storeItem.priceList;
      mergedPrice = storeItem.price;
      mergedApparelPrice = storeItem.apparelPrice ?? storeItem.price;
    } else if (shouldPreferCartPricing) {
      mergedPriceList = cartMatch.priceList;
      mergedPrice = cartMatch.price;
      mergedApparelPrice = cartMatch.apparelPrice ?? cartMatch.price;
    }

    return {
      ...cartMatch,
      id: storeItem.id || cartMatch.id,
      cartItemId: cartMatch.cartItemId || storeItem.cartItemId,
      proofItemId: storeItem.proofItemId || cartMatch.proofItemId,
      designId: storeItem.designId || cartMatch.designId,
      designGroupItemId: storeItem.designGroupItemId ?? cartMatch.designGroupItemId,
      proofId: cartMatch.proofId || storeItem.proofId,
      proofTitle: cartMatch.proofTitle || storeItem.proofTitle,
      apparelName: cartMatch.apparelName || storeItem.apparelName,
      productTitle: storeItem.productTitle || cartMatch.productTitle,
      productDescription: storeItem.productDescription || cartMatch.productDescription || '',
      apparelBrand: cartMatch.apparelBrand || storeItem.apparelBrand,
      apparelStyleCode: cartMatch.apparelStyleCode || storeItem.apparelStyleCode,
      apparelColor: cartMatch.apparelColor || storeItem.apparelColor,
      apparelHex: cartMatch.apparelHex || storeItem.apparelHex,
      apparelColorCode: cartMatch.apparelColorCode || storeItem.apparelColorCode,
      apparelSizes:
        storeItem.designApparelSizes || storeItem.apparelSizes || cartMatch.apparelSizes,
      designApparelSizes:
        storeItem.designApparelSizes ||
        cartMatch.designApparelSizes ||
        cartMatch.apparelSizes ||
        storeItem.apparelSizes,
      orderItemSizes: storeItem.orderItemSizes || cartMatch.orderItemSizes,
      // Prefer store mockups when present — cart can still hold a different color's blanks.
      primaryImage: storeItem.primaryImage || cartMatch.primaryImage,
      frontImage: storeItem.frontImage || cartMatch.frontImage,
      backImage: storeItem.backImage || cartMatch.backImage,
      designImgPrimary: storeItem.designImgPrimary || cartMatch.designImgPrimary,
      designImgSecondary: storeItem.designImgSecondary || cartMatch.designImgSecondary,
      designImages: storeItem.designImages ?? cartMatch.designImages,
      printTypes: cartMatch.printTypes || storeItem.printTypes,
      printTypeInfo: cartMatch.printTypeInfo?.length
        ? cartMatch.printTypeInfo
        : storeItem.printTypeInfo,
      decorMethodInfo: cartMatch.decorMethodInfo?.length
        ? cartMatch.decorMethodInfo
        : storeItem.decorMethodInfo,
      decoratingMethod: cartMatch.decoratingMethod || storeItem.decoratingMethod,
      priceList: mergedPriceList,
      price: mergedPrice,
      baseUnitPrice: cartMatch.baseUnitPrice ?? storeItem.baseUnitPrice,
      strikedPricePerUnit: cartMatch.strikedPricePerUnit ?? storeItem.strikedPricePerUnit,
      apparelPrice: mergedApparelPrice,
      quantity: mergedCartMinimum ?? storeItem.quantity ?? cartMatch.quantity,
      minQuant: mergedCartMinimum ?? storeItem.minQuant ?? cartMatch.minQuant,
      savedMinimumQuantity: storeItem.savedMinimumQuantity ?? cartMatch.savedMinimumQuantity,
      pricingType: storeItem.pricingType ?? cartMatch.pricingType,
      donationAmount: storeItem.donationAmount || cartMatch.donationAmount,
      designLicensing: cartMatch.designLicensing ?? storeItem.designLicensing,
      designPricing: cartMatch.designPricing ?? storeItem.designPricing,
      designRejectionInfo: cartMatch.designRejectionInfo ?? storeItem.designRejectionInfo,
      alphaNumericProofItemId:
        cartMatch.alphaNumericProofItemId || storeItem.alphaNumericProofItemId,
      isStore: true,
      isActive: true,
      is_selected: 1 as const,
    } as ICartItemDetail;
  });
};

export const resolveStoreDisplayMadeWithFromApi = (
  displayMadeWithLove: number | boolean | null | undefined
): boolean => {
  if (displayMadeWithLove === false || displayMadeWithLove === 0) {
    return false;
  }

  return true;
};

type IStoreFundraiserFields = IStoreResponse & {
  fundraiserCause?: string | null;
  fundraiserGoal?: string | number | null;
};

export const resolveFundraiserCauseFromStore = (store: IStoreFundraiserFields): string => {
  const cause = store.fundraiser_cause ?? store.fundraiserCause;
  return typeof cause === 'string' ? cause : '';
};

export const resolveFundraiserGoalFromStore = (store: IStoreFundraiserFields): string => {
  const goal = store.fundraiser_goal ?? store.fundraiserGoal;
  if (goal == null || goal === '') {
    return '';
  }

  return String(goal);
};

const resolveStoreOrderUntilFromApi = (store: IStoreResponse): string => {
  if (store.group_order_date) {
    return convertUtcIsoStringToStoreCheckoutCalendarDate(store.group_order_date);
  }

  if (store.due_date) {
    return convertUtcIsoStringToStoreCheckoutCalendarDate(store.due_date);
  }

  return '';
};

export const buildStoreInfoFormValuesFromStore = (
  store: IStoreResponse
): Partial<IStoreInfoFormShape> => ({
  storeName: store.name ?? '',
  storeDescription: store.description ?? '',
  orderUntil: resolveStoreOrderUntilFromApi(store),
  displayMadeWith: resolveStoreDisplayMadeWithFromApi(store.display_made_with_love),
  charityName: resolveFundraiserCauseFromStore(store),
  raiseAmount: resolveFundraiserGoalFromStore(store),
});

export interface IStoreCheckoutHydrationResult {
  cartItems: ICartItemDetail[];
  storeType: StoreCheckoutStoreType | '';
  storeInfo: Partial<IStoreInfoFormShape>;
  shippingOption: StoreShippingFormOption | '';
  donationAmounts: Record<string, string>;
}

export const buildStoreCheckoutHydrationFromStoreDetails = (
  store: IStoreResponse
): IStoreCheckoutHydrationResult => {
  const cartItems = mapStoreOrderItemsToCartItems(store);

  return {
    cartItems,
    storeType: resolveCheckoutStoreType(store.store_type),
    storeInfo: buildStoreInfoFormValuesFromStore(store),
    shippingOption: resolveStoreShippingOptionFromStore(store),
    donationAmounts: buildStoreReviewDonationAmountsFromCartItems(cartItems),
  };
};
